import Fetcher from "../fetcher";
import { type } from "../charge/utils";
import { naturalSort } from "./helpers";
import { isString, isPlainObject, withProperties, getValueAtPath } from "../utils";
import { DEFAULT_RETRY_CONDITIONS, isJsonResponse } from "../fetcher/utils";
import ErrorState from "../context/common/error";

const $fetcher = Fetcher.one
  .limit(5)
  .timeout(6000)
  .retry(DEFAULT_RETRY_CONDITIONS);

const countrySort = naturalSort('name', 'code');
const networkSort = naturalSort('label', 'name');

const NETWORKS_LIST = withProperties(Object.create(null), {
  GH: Object.freeze([
    { name: 'AIRTEL', identifier: 'TIGO', label: 'AIRTEL/TIGO' },
    { name: 'MTN', identifier: 'MTN', label: 'MTN' },
    { name: 'VODACOM', identifier: 'VODAFONE', label: 'TELECEL' }
  ].sort(networkSort)),

  KE: Object.freeze([
    // { name: 'AIRTEL', identifier: 'AIRTEL', label: 'AIRTEL' },
    { name: 'MPESA', identifier: 'SAFARICOM', label: 'M-PESA' }
  ].sort(networkSort)),
  
  TZ: Object.freeze([
    { name: 'AIRTEL', identifier: 'AIRTEL', label: 'AIRTEL' },
    { name: 'TIGOPESA', identifier: 'TIGOPESA', label: 'TIGO' },
    { name: 'HALOPESA', identifier: 'HALOPESA', label: 'HALOPESA' }
  ].sort(networkSort)),

  ZM: Object.freeze([
    { name: 'AIRTEL', identifier: 'AIRTEL', label: 'AIRTEL' },
    { name: 'MTN', identifier: 'MTN', label: 'MTN' },
    { name: 'ZAMTEL', identifier: 'ZAMTEL', label: 'ZAMTEL' }
  ].sort(networkSort))
});

// Francophone countries carry their currency in FRANCO_COUNTRIES_LIST instead.
const NETWORK_CURRENCIES = Object.freeze({ GH: 'GHS', KE: 'KES', TZ: 'TZS', ZM: 'ZMW' });

const FRANCO_COUNTRIES_LIST = Object.freeze([
  { name: 'Burkina Faso', code: 'BF', currency: 'XOF' },
  { name: 'Cameroon', code: 'CM', currency: 'XAF' },
  { name: "Côte d'Ivoire", code: 'CI', currency: 'XOF' },
  // { name: 'Gabon', code: 'GA', currency: 'XAF' },
  // { name: 'Mali', code: 'ML', currency: 'XOF' },
  // { name: 'Mauritius', code: 'MU' },
  { name: 'Senegal', code: 'SN', currency: 'XOF' }
].sort(countrySort));

function fetchFrancophoneNetworks(context) {
  return Promise.resolve(
    Object.isFrozen(NETWORKS_LIST) ||
    $fetcher(new URL('/services/fetch/mobilemoneyfranco/networks', process.env.CHECKOUT_BASE_URL))
      .then(response => {
        if (isJsonResponse(response) && response.ok) {
          return response.json()
            .then(response => {
              let fetched = false;
              const { data, response_code } = isPlainObject(response) ? response : {};

              if (response_code === '00' && type(data) === 'array') {
                data.forEach(item => {
                  if (isPlainObject(item)) {
                    let { operators, country_code } = item;

                    if (/^[a-z]{2}$/i.test(country_code) && type(operators) === 'array') {
                      operators = operators.reduce((list, operator) => {
                        if (isPlainObject(operator)) {
                          const { name, code } = operator;

                          if (name && code && isString(name) && isString(code)) {
                            list.push({ name, identifier: code, label: name });
                          }
                        }

                        return list;
                      }, []);

                      if (operators.length > 0) {
                        try {
                          withProperties(NETWORKS_LIST, {
                            [country_code.toUpperCase()]: Object.freeze(operators.sort(networkSort))
                          });

                          fetched !== true && (fetched = true);
                        } catch (e) {}
                      }
                    }
                  }
                });
              }

              return !!(fetched === true && Object.freeze(NETWORKS_LIST));
            })
        }
      })
      .catch(() => {
        setTimeout(() => {
            if(['XAF', 'XOF'].includes(getValueAtPath(context, 'DATA.currency'))) {
              context.dispatch(
                ErrorState.actions.updateErrorMessage('network.fetch.error')
              )
            }
        }, 100)
      })
  );
}

function selectCountryFromList(list, country) {
  if (type(list) !== 'array') return null;

  if (list.indexOf(country) < 0) {
    const { name: countryName, code: countryCode } = isPlainObject(country) ? country : (
      /^[A-Z]{2}$/.test(country)
        ? { code: country }
        : { name: String(country) }
    );

    country = list.find(item => {
      if (isPlainObject(item)) {
        const { name, code } = item;

        const hasName = typeof countryName !== 'undefined';
        const hasCode = typeof countryCode !== 'undefined';

        let matches = (hasName || hasCode) ? 1 : 0;

        hasName && (matches = matches & (countryName === name));
        hasCode && (matches = matches & (countryCode === code));

        return matches;
      }
    });
  }

  return country || null;
}

function selectNetworkFromList(list, network) {
  if (type(list) !== 'array') return null;

  if (list.indexOf(network) < 0) {
    const isNetworkObject = isPlainObject(network);

    const { name: networkName, identifier: networkIdentifier } =
      isNetworkObject
        ? network
        : (network = String(network), { name: network, identifier: network });

    network = list.find(item => {
      if (isPlainObject(item)) {
        const { name, identifier } = item;

        const hasName = typeof networkName !== 'undefined';
        const hasIdentifier = typeof networkIdentifier !== 'undefined';

        let matches = (hasName || hasIdentifier) ? 1 : 0;

        if (isNetworkObject) {
          hasName && (matches = matches & (networkName === name));
          hasIdentifier && (matches = matches & (networkIdentifier === identifier));
        } else {
          matches = matches & (
            hasName && networkName === name ||
            hasIdentifier && networkIdentifier === identifier
          );
        }

        return matches;
      }
    });
  }

  return network || null;
}

// Backend sends display names (TELECEL) while GH's name/identifier differ, so match all three.
// Nothing matching means we can't honour the restriction; an empty picker would block payment.
function filterAllowedNetworks(list, allowed) {
  if (type(list) !== 'array' || type(allowed) !== 'array') return list;

  const names = allowed.filter(isString).map(name => name.trim().toUpperCase()).filter(Boolean);
  if (names.length === 0) return list;

  const filtered = list.filter(network => (
    isPlainObject(network) &&
    [network.name, network.identifier, network.label]
      .some(value => isString(value) && names.includes(value.toUpperCase()))
  ));

  return filtered.length > 0 ? Object.freeze(filtered) : list;
}

// `momo_networks` from initialize is keyed by currency; a currency it omits keeps every network.
function restrictNetworksList(momoNetworks) {
  if (!isPlainObject(momoNetworks)) return NETWORKS_LIST;

  return Object.freeze(Object.keys(NETWORKS_LIST).reduce((restricted, country) => withProperties(restricted, {
    [country]: filterAllowedNetworks(NETWORKS_LIST[country], momoNetworks[getNetworkCurrency(country)])
  }), Object.create(null)));
}

function getNetworkCurrency(country) {
  return NETWORK_CURRENCIES[country] ||
    getValueAtPath(selectCountryFromList(FRANCO_COUNTRIES_LIST, country), 'currency') ||
    null;
}

export {
  NETWORKS_LIST,
  FRANCO_COUNTRIES_LIST,
  selectCountryFromList,
  selectNetworkFromList,
  filterAllowedNetworks,
  restrictNetworksList,
  fetchFrancophoneNetworks
}